import _ from 'lodash';
import React, {FunctionComponent, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Grid, makeStyles} from '@material-ui/core';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';
import {
    ExtractionMethod,
    OwnerType,
    ProductAttributesField,
    ProductData,
    ProductDetailsField,
    ProductField,
    ProductInCatalog,
} from '@treez/commons/sharedTypings/product';
import {
    NumberInput,
    ProductClassificationSelect,
    ProductSubtypeSelect,
    ProductTypeSelect,
    SelectInput,
    TextInput,
    TagInput,
} from '@treez-ui/core/forms';
import {TreezColor, treezColorMap} from '~/styles/constants';
import {HandleProductValidationFunction, ProductValidationErrorReport} from '~/lib/errors';
import {formatNumberToPrecision} from '~/lib/format';
import ImageGalleryGridCell, {
    ImageGalleryGridCellOption,
} from '~/modules/pages/productMerge/mergeProductGrid/mergeProductColumn/ImageGalleryGridCell';
import LongTextValue from '~/modules/pages/productMerge/mergeProductGrid/mergeProductColumn/MergeDescriptionCell';
import {removeProductSource, updateMergeProduct} from '~/redux/actions/MergeActions';
import {
    selectMergeExactMatchFields,
    selectMergingToOrgProduct,
} from '~/redux/selectors/mergeSelectors';
import {selectProductMetaData} from '~/redux/selectors/ProductMetaDataSelectors';
import OwnerTypeCell from '../mergeProductGrid/mergeProductColumn/OwnerTypeCell';
import {getValidUomOptions} from '~/utils/ProductAPI';

const {
    AMOUNT,
    BRAND_NAME,
    CLASSIFICATION,
    DESCRIPTIONS,
    ECOMMERCE_NAME,
    NAME,
    SIZE,
    SKU,
    SUBTYPE,
    TYPE,
} = ProductField;

const {EFFECT, FLAVOR, GENERAL, INGREDIENT, INTERNAL} = ProductAttributesField;

const {
    DOSES,
    DOSE_CBD_MG,
    DOSE_THC_MG,
    EXTRACTION_METHOD,
    THC_MG,
    CBD_MG,
    STRAIN,
    NET_WEIGHT,
    GROSS_WEIGHT,
    TOTAL_WEIGHT,
    TOTAL_CONCENTRATE_WEIGHT,
} = ProductDetailsField;

interface Props {
    draftProduct: Partial<ProductInCatalog>;
    handleInput: (field: ProductData, value: any) => void;
    handleValidationError: HandleProductValidationFunction;
    hasBeenSubmitted: boolean;
    hideExactMatch: boolean;
    invalidFields: ProductData[];
    productValidationErrors: ProductValidationErrorReport;
    requiredFields: ProductData[];
}

export const MergeProductForm: FunctionComponent<Props> = (props: Props) => {
    const {
        draftProduct,
        handleInput,
        handleValidationError,
        hasBeenSubmitted,
        hideExactMatch,
        invalidFields,
        productValidationErrors,
        requiredFields,
    } = props;

    const exactMatchedFields = useSelector(selectMergeExactMatchFields, _.isEqual);
    const productMetaData = useSelector(selectProductMetaData);
    const mergingToExistingOrgProduct = useSelector(selectMergingToOrgProduct);

    const [valueBeforeFocus, setValueBeforeFocus] = useState<any>();

    const {
        generalAttributesOptions,
        flavorAttributesOptions,
        ingredientAttributesOptions,
        effectAttributesOptions,
        internalAttributesOptions,
    } = productMetaData;

    const dispatch = useDispatch();

    const hideField = (field: ProductData): boolean => {
        const fieldIsInvalid = _.includes(invalidFields, field);
        const fieldIsExactMatch = _.includes(exactMatchedFields, field);

        return fieldIsInvalid || (fieldIsExactMatch && hideExactMatch);
    };

    const fieldIsRequiredPerType = (field: ProductData): boolean => {
        return _.includes(requiredFields, field);
    };

    const handleBlur = (field) => {
        let currentValue;

        if (_.includes(ProductDetailsField, field)) {
            currentValue = _.get(draftProduct.details, field);
        } else {
            currentValue = draftProduct[field];
        }
        dispatch(updateMergeProduct(field, currentValue));

        if (currentValue !== valueBeforeFocus) {
            dispatch(removeProductSource(field));
        }

        setValueBeforeFocus(undefined);
    };

    const handleFocus = (field: ProductField | ProductDetailsField) => {
        let currentValue;
        if (_.includes(ProductDetailsField, field)) {
            currentValue = _.get(draftProduct.details, field);
        } else {
            currentValue = draftProduct[field];
        }

        setValueBeforeFocus(currentValue);
    };

    const {
        attributes,
        brandName,
        classification,
        descriptions = {main: ''},
        details = {},
        eCommerceName,
        images,
        name,
        size,
        sku,
        subtype,
        type,
        uom,
    } = draftProduct;

    const amount = draftProduct.amount
        ? formatNumberToPrecision(draftProduct.amount, 2)
        : undefined;
    const cbdMg = _.get(details, 'cbdMg');
    const doseCbdMg = _.get(details, 'doseCbdMg');
    const doses = _.get(details, 'doses');
    const doseThcMg = _.get(details, 'doseThcMg');
    const extractionMethod = _.get(details, 'extractionMethod');
    const grossWeight = _.get(details, 'grossWeight');
    const netWeight = _.get(details, 'netWeight');
    const strain = _.get(details, 'strain');
    const thcMg = _.get(details, 'thcMg');
    const totalWeight = _.get(details, 'totalWeight');
    const totalConcentrateWeight = _.get(details, 'totalConcentrateWeight');

    const productEffects = _.get(attributes, EFFECT, []) as string[];
    const productIngredients = _.get(attributes, INGREDIENT, []) as string[];
    const productFlavors = _.get(attributes, FLAVOR, []) as string[];
    const productGeneralAttributes = _.get(attributes, GENERAL, []) as string[];
    const productInternalAttributes = _.get(attributes, INTERNAL, []) as string[];

    const uomOptions = getValidUomOptions();

    const {TREEZ_GREEN, GREY_MEDIUM} = TreezColor;

    const treezGreen = treezColorMap[TREEZ_GREEN];
    const mediumGray = treezColorMap[GREY_MEDIUM];

    const useStyles = makeStyles({
        descriptionContainer: {
            height: '400px',
            padding: '8px',
        },
        feedbackContainer: {
            alignItems: 'center',
            backgroundColor: treezGreen,
            borderBottom: `1px solid ${mediumGray}`,
            color: 'white',
            display: 'flex',
            height: '110px',
            justifyContent: 'center',
        },
        inputContainer: {
            alignItems: 'center',
            padding: '8px',
            width: '100%',
            boxSizing: 'border-box',
        },
        multiValueContainer: {
            height: '200px',
            padding: '8px',
            width: '100%',
        },
        productFormColumn: {
            border: `2px solid ${treezGreen}`,
            borderRadius: '10px 10px 0 0',
            marginTop: '-2px',
            width: '100%',
        },
        singleInputContainer: {
            alignItems: 'center',
            minHeight: '80px',
            height: 'auto',
        },
    });

    const {
        descriptionContainer,
        feedbackContainer,
        inputContainer,
        multiValueContainer,
        productFormColumn,
        singleInputContainer,
    } = useStyles();

    const title = mergingToExistingOrgProduct
        ? 'Merging To Existing Product'
        : 'New Merged Product';

    return (
        <Grid
            item
            container
            align-content="flex-start"
            className={productFormColumn}
            justifyContent="flex-start"
            sm={12}
            spacing={0}
        >
            <ImageGalleryGridCell images={images} editOrSelect={ImageGalleryGridCellOption.EDIT} />
            <Grid item className={feedbackContainer} xs={12}>
                <Title text={title} size={TitleSize.SMALL} />
            </Grid>
            <Grid item className={singleInputContainer} xs={12}>
                <OwnerTypeCell ownerType={OwnerType.ORG} productForm />
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(BRAND_NAME)} xs={12}>
                <Grid className={inputContainer}>
                    <TextInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(BRAND_NAME, errorPresent, errorMessage)
                        }
                        label="Brand"
                        onBlur={() => handleBlur(BRAND_NAME)}
                        onFocus={() => handleFocus(BRAND_NAME)}
                        onChange={(value) => handleInput(BRAND_NAME, value)}
                        required={fieldIsRequiredPerType(BRAND_NAME)}
                        value={brandName}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(NAME)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <TextInput
                        required
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(NAME, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, NAME)}
                        label="Name"
                        onBlur={() => handleBlur(NAME)}
                        onFocus={() => handleFocus(NAME)}
                        onChange={(value) => handleInput(NAME, value)}
                        value={name}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(ECOMMERCE_NAME)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <TextInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(ECOMMERCE_NAME, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, ECOMMERCE_NAME)}
                        label="Menu Title"
                        onBlur={() => handleBlur(ECOMMERCE_NAME)}
                        onFocus={() => handleFocus(ECOMMERCE_NAME)}
                        onChange={(value) => handleInput(ECOMMERCE_NAME, value)}
                        required={fieldIsRequiredPerType(ECOMMERCE_NAME)}
                        value={eCommerceName}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(TYPE)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <ProductTypeSelect
                        required
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(TYPE, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, TYPE)}
                        onChange={(typeSelected) => handleInput(TYPE, typeSelected)}
                        onClear={() => {
                            handleInput(TYPE, undefined);
                            handleInput(SUBTYPE, undefined);
                        }}
                        value={type}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(SUBTYPE)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <ProductSubtypeSelect
                        dirty={hasBeenSubmitted}
                        disabled={!type}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(SUBTYPE, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, SUBTYPE)}
                        onChange={(subtypeSelected) => handleInput(SUBTYPE, subtypeSelected)}
                        onClear={() => handleInput(SUBTYPE, undefined)}
                        productType={type}
                        required={fieldIsRequiredPerType(SUBTYPE)}
                        value={subtype}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(SIZE)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <TextInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(SIZE, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, SIZE)}
                        label="Size"
                        onBlur={() => handleBlur(SIZE)}
                        onFocus={() => handleFocus(SIZE)}
                        onChange={(value) => handleInput(SIZE, value)}
                        required={fieldIsRequiredPerType(SIZE)}
                        value={size}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(AMOUNT)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(AMOUNT, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, AMOUNT)}
                        label="Amount"
                        onBlur={() => handleBlur(AMOUNT)}
                        onFocus={() => handleFocus(AMOUNT)}
                        onChange={(value) => handleInput(AMOUNT, value)}
                        required={fieldIsRequiredPerType(AMOUNT)}
                        value={amount ? String(amount) : undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(CLASSIFICATION)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <ProductClassificationSelect
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(CLASSIFICATION, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, CLASSIFICATION)}
                        onChange={(classificationSelected) =>
                            handleInput(CLASSIFICATION, classificationSelected)
                        }
                        onClear={() => handleInput(CLASSIFICATION, undefined)}
                        required={fieldIsRequiredPerType(CLASSIFICATION)}
                        value={classification}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(ProductField.UOM)}
                xs={12}
                sm={12}
            >
                <Grid item className={inputContainer} xs={12} sm={12}>
                    <SelectInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(ProductField.UOM, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, ProductField.UOM)}
                        label="UoM"
                        onClear={() => handleInput(ProductField.UOM, undefined)}
                        onChange={(value) => handleInput(ProductField.UOM, value)}
                        options={uomOptions}
                        required={fieldIsRequiredPerType(ProductField.UOM)}
                        value={uom}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(SKU)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <TextInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(SKU, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, SKU)}
                        label="SKU"
                        onBlur={() => handleBlur(SKU)}
                        onFocus={() => handleFocus(SKU)}
                        onChange={(value) => handleInput(SKU, value)}
                        required={fieldIsRequiredPerType(SKU)}
                        value={sku}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(DOSES)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(DOSES, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, DOSES)}
                        label="Doses"
                        minimum={1}
                        onBlur={() => handleBlur(DOSES)}
                        onFocus={() => handleFocus(DOSES)}
                        onChange={(value) => handleInput(DOSES, value)}
                        required={fieldIsRequiredPerType(DOSES)}
                        value={doses as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(CBD_MG)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(CBD_MG, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, CBD_MG)}
                        label="CBD mg"
                        onBlur={() => handleBlur(CBD_MG)}
                        onFocus={() => handleFocus(CBD_MG)}
                        onChange={(value) => handleInput(CBD_MG, value)}
                        required={fieldIsRequiredPerType(CBD_MG)}
                        value={cbdMg as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid item className={singleInputContainer} hidden={hideField(THC_MG)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(THC_MG, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, THC_MG)}
                        label="THC mg"
                        onBlur={() => handleBlur(THC_MG)}
                        onFocus={() => handleFocus(THC_MG)}
                        onChange={(value) => handleInput(THC_MG, value)}
                        required={fieldIsRequiredPerType(THC_MG)}
                        value={thcMg as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(DOSE_CBD_MG)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(DOSE_CBD_MG, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, DOSE_CBD_MG)}
                        label="Dose CBD mg"
                        onBlur={() => handleBlur(DOSE_CBD_MG)}
                        onFocus={() => handleFocus(DOSE_CBD_MG)}
                        onChange={(value) => handleInput(DOSE_CBD_MG, value)}
                        required={fieldIsRequiredPerType(DOSE_CBD_MG)}
                        value={doseCbdMg as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(DOSE_THC_MG)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(DOSE_THC_MG, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, DOSE_THC_MG)}
                        label="Dose THC mg"
                        onBlur={() => handleBlur(DOSE_THC_MG)}
                        onFocus={() => handleFocus(DOSE_THC_MG)}
                        onChange={(value) => handleInput(DOSE_THC_MG, value)}
                        required={fieldIsRequiredPerType(DOSE_THC_MG)}
                        value={doseThcMg as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(EXTRACTION_METHOD)}
                xs={12}
            >
                <Grid className={inputContainer}>
                    <SelectInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(EXTRACTION_METHOD, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, EXTRACTION_METHOD)}
                        label="Extraction Method"
                        onChange={(value) => handleInput(EXTRACTION_METHOD, value)}
                        options={_.values(ExtractionMethod)}
                        required={fieldIsRequiredPerType(EXTRACTION_METHOD)}
                        value={extractionMethod}
                        onClear={() => handleInput(EXTRACTION_METHOD, undefined)}
                    />
                </Grid>
            </Grid>
            {/* AZ */}
            <Grid item className={singleInputContainer} hidden={hideField(STRAIN)} xs={12} sm={12}>
                <Grid className={inputContainer}>
                    <TextInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(STRAIN, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, STRAIN)}
                        label="Strain"
                        onBlur={() => handleBlur(STRAIN)}
                        onFocus={() => handleFocus(STRAIN)}
                        onChange={(value) => handleInput(STRAIN, value)}
                        required={fieldIsRequiredPerType(STRAIN)}
                        value={strain}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(NET_WEIGHT)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(NET_WEIGHT, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, NET_WEIGHT)}
                        label="Net Weight"
                        onBlur={() => handleBlur(NET_WEIGHT)}
                        onFocus={() => handleFocus(NET_WEIGHT)}
                        onChange={(value) => handleInput(NET_WEIGHT, value)}
                        required={fieldIsRequiredPerType(NET_WEIGHT)}
                        value={netWeight as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(GROSS_WEIGHT)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(GROSS_WEIGHT, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, GROSS_WEIGHT)}
                        label="Gross Weight"
                        onBlur={() => handleBlur(GROSS_WEIGHT)}
                        onFocus={() => handleFocus(GROSS_WEIGHT)}
                        onChange={(value) => handleInput(GROSS_WEIGHT, value)}
                        required={fieldIsRequiredPerType(GROSS_WEIGHT)}
                        value={grossWeight as string | undefined}
                    />
                </Grid>
            </Grid>
            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(TOTAL_WEIGHT)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(TOTAL_WEIGHT, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, TOTAL_WEIGHT)}
                        label="Total Flower Weight"
                        onBlur={() => handleBlur(TOTAL_WEIGHT)}
                        onFocus={() => handleFocus(TOTAL_WEIGHT)}
                        onChange={(value) => handleInput(TOTAL_WEIGHT, value)}
                        required={fieldIsRequiredPerType(TOTAL_WEIGHT)}
                        value={totalWeight as string | undefined}
                    />
                </Grid>
            </Grid>

            <Grid
                item
                className={singleInputContainer}
                hidden={hideField(TOTAL_CONCENTRATE_WEIGHT)}
                xs={12}
                sm={12}
            >
                <Grid className={inputContainer}>
                    <NumberInput
                        dirty={hasBeenSubmitted}
                        handleError={(errorPresent, errorMessage) =>
                            handleValidationError(TOTAL_CONCENTRATE_WEIGHT, errorPresent, errorMessage)
                        }
                        helperText={_.get(productValidationErrors, TOTAL_CONCENTRATE_WEIGHT)}
                        label="Total Concentrate Weight"
                        onBlur={() => handleBlur(TOTAL_CONCENTRATE_WEIGHT)}
                        onFocus={() => handleFocus(TOTAL_CONCENTRATE_WEIGHT)}
                        onChange={(value) => handleInput(TOTAL_CONCENTRATE_WEIGHT, value)}
                        required={fieldIsRequiredPerType(TOTAL_CONCENTRATE_WEIGHT)}
                        value={totalConcentrateWeight as string | undefined}
                    />
                </Grid>
            </Grid>

            <Grid
                item
                className={descriptionContainer}
                hidden={hideField(DESCRIPTIONS)}
                xs={12}
                sm={12}
            >
                <LongTextValue clickOrEdit="edit" text={_.get(descriptions, 'main', '')} />
            </Grid>
            <Grid item className={multiValueContainer} hidden={hideField(EFFECT)} xs={12} sm={12}>
                <TagInput
                    label="Effects"
                    onChange={(values) => handleInput(EFFECT, values)}
                    options={effectAttributesOptions}
                    placeholder="Add an Effect"
                    values={productEffects}
                />
            </Grid>
            <Grid item className={multiValueContainer} hidden={hideField(FLAVOR)} xs={12} sm={12}>
                <TagInput
                    label="Flavors"
                    onChange={(values) => handleInput(FLAVOR, values)}
                    options={flavorAttributesOptions}
                    placeholder="Add a Flavor"
                    values={productFlavors}
                />
            </Grid>
            <Grid
                item
                className={multiValueContainer}
                hidden={hideField(INGREDIENT)}
                xs={12}
                sm={12}
            >
                <TagInput
                    label="Ingredients"
                    onChange={(values) => handleInput(INGREDIENT, values)}
                    options={ingredientAttributesOptions}
                    placeholder="Add an Ingredient"
                    values={productIngredients}
                />
            </Grid>
            <Grid item className={multiValueContainer} hidden={hideField(GENERAL)} xs={12} sm={12}>
                <TagInput
                    label="General Attributes"
                    onChange={(values) => handleInput(GENERAL, values)}
                    options={generalAttributesOptions}
                    placeholder="Add an Attribute"
                    values={productGeneralAttributes}
                />
            </Grid>
            <Grid item className={multiValueContainer} hidden={hideField(INTERNAL)} xs={12} sm={12}>
                <TagInput
                    label="Internal Attributes"
                    onChange={(values) => handleInput(INTERNAL, values)}
                    options={internalAttributesOptions}
                    placeholder="Add an Attribute"
                    values={productInternalAttributes}
                />
            </Grid>
        </Grid>
    );
};

export default MergeProductForm;
